import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('adminToken'));
  const [admin, setAdmin] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch user's effective permissions
  const fetchPermissions = useCallback(async (authToken) => {
    try {
      const response = await axios.get(`${API}/auth/me/permissions`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setPermissions(response.data.effective_permissions || {});
      return response.data;
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      setPermissions({});
      return null;
    }
  }, []);

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAdmin(response.data);
          
          // Fetch permissions after getting admin data
          await fetchPermissions(token);
        } catch (error) {
          localStorage.removeItem('adminToken');
          setToken(null);
          setAdmin(null);
          setPermissions(null);
        }
      }
      setLoading(false);
    };
    verifyToken();
  }, [token, fetchPermissions]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token } = response.data;
    localStorage.setItem('adminToken', access_token);
    setToken(access_token);
    
    // Fetch admin details
    const adminResponse = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${access_token}` }
    });
    setAdmin(adminResponse.data);
    
    // Fetch permissions
    await fetchPermissions(access_token);
    
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    setToken(null);
    setAdmin(null);
    setPermissions(null);
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    return response.data;
  };

  // Refresh permissions (useful after permission changes)
  const refreshPermissions = async () => {
    if (token) {
      await fetchPermissions(token);
    }
  };

  return (
    <AuthContext.Provider value={{
      token,
      admin,
      permissions,
      isAuthenticated: !!token,
      loading,
      login,
      logout,
      register,
      refreshPermissions
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
